/* ★ GAMBAR DARIPADA JAWAPAN AGENT (Agent Saya, 2026-09-11)
 *
 * Pelanggan menanda pusingan perbualan mana (jawapan pertama / selepas balasan
 * ke-N) yang gambarnya patut disimpan ke dalam kad sebagai GAMBAR TAMBAHAN
 * (`extraImageUrls`). Extension menunggu gambar itu siap dijana di ChatGPT /
 * Gemini, memampatnya, dan memulangkannya bersama teks jawapan; webapp pula
 * memuat naik ke Storage dan menulis alamatnya pada kad.
 *
 * Satu fail untuk DUA laluan yang menghantar tugasan agent (larian Configure di
 * PRO Studio + larian pukal Workspace) — kalau setiap satu mengira pusingannya
 * sendiri, satu akan terpesong senyap (corak `gabungArahanAgent`).
 *
 * Model data pada doc agent (users/{uid}/chatgptTemplates/{id}):
 *   firstImage  : boolean   — simpan gambar daripada jawapan PERTAMA
 *   replyImages : boolean[] — sejajar dengan `replies`; true = simpan gambar
 *                             daripada jawapan bagi balasan itu
 * Payload ke extension: `imageTurns: number[]` (0 = jawapan pertama, k = selepas
 * balasan ke-k, dikira atas balasan yang TIDAK kosong sahaja — balasan kosong
 * memang tidak dihantar, jadi ia tidak wujud sebagai pusingan).
 *
 * Peraturan (keputusan user 2026-09-11):
 *   · Larian yang ada pusingan bertanda MENAMBAH gambar ke kad (bukan ganti)
 *     sehingga had 5 gambar termasuk gambar utama = 4 tambahan. Penuh → gambar
 *     baharu TIDAK disimpan dan kad diberitahu dengan jelas (bukan senyap).
 *   · Larian TANPA pusingan bertanda tidak menyentuh gambar tambahan langsung.
 *   · Gambar dimampat di extension (≤1400px, JPEG) SEBELUM dihantar — saiz
 *     ≈200-400KB — dan dikira dalam kuota storan 5GB (kaunter naik semasa
 *     muat naik; padam kad menolak semula melalui `collectProductStorageUrls`).
 */
import { ref as fbRef, uploadString, getDownloadURL } from 'firebase/storage';

/* Had gambar TAMBAHAN setiap kad (utama + 4 = 5). ⚠️ KEMBAR: extension
   ProductCard.tsx memotong `slice(0, 4)` dan WorkspaceView `GBR_MAKS_TAMBAHAN`
   membaca pemalar ini — ubah di sini sahaja. */
export const MAKS_GAMBAR_TAMBAHAN = 4;

/* Normalisasi medan gambar daripada doc agent. `replies` = senarai balasan
   yang SUDAH dinormalisasi, supaya `replyImages` sentiasa sejajar dengannya. */
export function normGambarAgent(d, replies) {
    const n = Array.isArray(replies) ? replies.length : 0;
    const raw = Array.isArray(d?.replyImages) ? d.replyImages : [];
    const replyImages = [];
    for (let i = 0; i < n; i++) replyImages.push(!!raw[i]);
    return { firstImage: !!d?.firstImage, replyImages };
}

/* Pusingan yang gambarnya disimpan → nombor pusingan seperti yang extension
   kira (0 = jawapan pertama; k = jawapan selepas balasan tidak-kosong ke-k). */
export function imageTurnsDaripadaAgent(a) {
    if (!a) return [];
    const replies = Array.isArray(a.replies) ? a.replies : [];
    const flags = Array.isArray(a.replyImages) ? a.replyImages : [];
    const turns = [];
    if (a.firstImage) turns.push(0);
    let k = 0;
    replies.forEach((r, i) => {
        if (!String(r || '').trim()) return;   // balasan kosong tidak dihantar = bukan pusingan
        k += 1;
        if (flags[i]) turns.push(k);
    });
    return turns;
}

/* Bilangan pusingan bertanda — dipakai skrin PRO Studio (kiraan + had). */
export function bilanganGambarDitanda(a) {
    return imageTurnsDaripadaAgent(a).length;
}

/* ★ CUBA SEMULA GAMBAR (2026-09-13) — bila ChatGPT/Gemini SELESAI menjawab tanpa
   gambar (cth "the image we created may violate our content policies"), extension
   menghantar semula mesej pusingan itu sehingga 3 kali.
   ⚠️ KEMBAR: extension `src/chrome/cubaSemulaGambar.ts` (Gemini) + skrip remote
   `src/chrome/chatgpt-automation.js` (ChatGPT) (CUBA_SEMULA_GAMBAR) — nombor
   mesti SAMA. Kalau extension mencuba lebih banyak kali daripada yang dikira di sini,
   webapp membatalkan larian yang sebenarnya masih berjalan. */
export const CUBA_SEMULA_GAMBAR = 3;
/* Jaring setiap cuba semula: hantar semula + gambar dijana (≈1–2 minit) + pengesanan. */
const MS_SETIAP_CUBA_SEMULA = 180000;

/* Had masa bagi pusingan bergambar: jaring 5 minit menunggu gambar + ruang untuk
   setiap cuba semula. SATU takrifan untuk KEDUA-DUA laluan (Configure + Workspace). */
export function hadMasaPusinganGambar(bilPusingan) {
    return Math.max(0, Number(bilPusingan) || 0) * (300000 + CUBA_SEMULA_GAMBAR * MS_SETIAP_CUBA_SEMULA);
}

/* Satu baris log kad bagi pusingan yang BERJAYA selepas dicuba semula — supaya
   pelanggan tahu kenapa perbualan GPT/Gem membawa mesej berulang. '' = tiada.
   (Pusingan yang GAGAL selepas 3 kali tidak sampai ke sini — seluruh tugasan gagal
   dan sebabnya dilog sebagai ralat.) */
export function ringkasCubaSemulaGambar(laporan) {
    const ok = (Array.isArray(laporan) ? laporan : []).filter((l) => l && l.berjaya && Number(l.kali) > 0);
    if (!ok.length) return '';
    return 'Gambar agent dijana selepas dicuba semula: ' + ok.map((l) => {
        const nama = Number(l.turn) === 0 ? 'jawapan pertama' : `jawapan selepas balasan ${l.turn}`;
        const sebab = String((Array.isArray(l.sebab) ? l.sebab[0] : '') || '').slice(0, 100);
        return `${nama} (${l.kali} kali${sebab ? ` — "${sebab}"` : ''})`;
    }).join(' · ');
}

/* Saiz bait sebenar sebuah data URL (selepas nyahkod base64). */
export function saizDataUrl(dataUrl) {
    const idx = String(dataUrl || '').indexOf(',');
    if (idx < 0) return 0;
    return Math.round((dataUrl.length - idx - 1) * 0.75);
}

/* Muat naik gambar yang extension pulangkan ke Storage + kira kuota.
 *   images   : [{ turn, dataUrl, w, h, bytes }] daripada extension
 *   sediaAda : `extraImageUrls` kad sekarang (ditambah, BUKAN diganti)
 *   bump     : fungsi kaunter storan (WorkspaceView `bumpStorageCounter`)
 * Pulangan: { urls (senarai penuh baharu), baru, disimpan, ditolak, gagal, bytes }
 *   ditolak = gambar yang TIDAK dimuat naik kerana had 4 tambahan sudah penuh.
 *   gagal   = muat naik yang melontar ralat (dicuba satu-satu; satu gagal tidak
 *             menggagalkan yang lain).
 * Laluan simpanan `users/{uid}/products/{productId}/agent_*.jpg` — di bawah
 * `users/{uid}/**` (skop bar 5GB + rules pemilik), dan Optimize membaca SEMUA
 * alamat pada doc kad, jadi fail ini dikira "dipakai", bukan yatim. */
export async function simpanGambarAgentKeKad({ storage, uid, productId, images, sediaAda = [], bump }) {
    const asal = Array.isArray(sediaAda) ? sediaAda.filter(u => typeof u === 'string' && u) : [];
    const senarai = (Array.isArray(images) ? images : [])
        .filter(g => g && typeof g.dataUrl === 'string' && /^data:image\//i.test(g.dataUrl));
    const ruang = Math.max(0, MAKS_GAMBAR_TAMBAHAN - asal.length);
    const untukNaik = senarai.slice(0, ruang);
    const ditolak = senarai.length - untukNaik.length;
    const baru = [];
    let gagal = 0;
    let bytes = 0;
    for (let i = 0; i < untukNaik.length; i++) {
        const g = untukNaik[i];
        const mime = (g.dataUrl.match(/^data:(image\/[a-z0-9.+-]+);/i) || [])[1] || 'image/jpeg';
        const ext = mime === 'image/png' ? 'png' : 'jpg';
        const path = `users/${uid}/products/${productId}/agent_${Date.now()}_${i}.${ext}`;
        try {
            const r = fbRef(storage, path);
            await uploadString(r, g.dataUrl, 'data_url', {
                contentType: mime,
                cacheControl: 'public, max-age=31536000, immutable',   // nama fail bercap masa = kekal
            });
            const url = await getDownloadURL(r);
            const saiz = saizDataUrl(g.dataUrl);
            bytes += saiz;
            baru.push(url);
            if (typeof bump === 'function' && saiz > 0) bump(saiz);
        } catch (e) {
            gagal += 1;
            console.warn('[AgentGambar] muat naik gagal:', e?.message || e);
        }
    }
    return { urls: [...asal, ...baru], baru, disimpan: baru.length, ditolak, gagal, bytes };
}

/* ★ GAMBAR PRODUK YANG DIHANTAR KEPADA AGENT (2026-09-13)
 *
 * Kad / produk yang membawa gambar TAMBAHAN → extension menghantar sehingga 5
 * gambar (utama + tambahan) ke ChatGPT/Gemini dan memulangkan laporan
 * `gambarProduk = { diminta, pendua, dilampirkan, amaran[] }`.
 * ⚠️ KEMBAR BENTUK: `HasilGambarProduk` dalam extension
 *    src/chrome/gambarProdukAgent.ts — ubah satu, ubah dua.
 *
 * Satu takrifan untuk DUA pembaca (log kad Workspace + skrin Configure PRO Studio).
 * Pulang null bila tiada gambar tambahan (laluan satu-gambar lama — tiada laporan).
 *   status 'lama'    → extension tidak memulangkan laporan (versi lama)
 *   status 'kurang'  → sebahagian gambar tidak sampai, ATAU ada amaran
 *   status 'lengkap' → semua gambar unik sampai tanpa amaran
 */
export function ringkasGambarProduk(bilTambahan, gp) {
    if (!(Number(bilTambahan) > 0)) return null;
    if (!gp || typeof gp !== 'object') return { status: 'lama', dihantar: 0, dijangka: 0, pendua: 0, amaran: [] };
    const diminta = Math.max(0, Number(gp.diminta) || 0);
    const pendua = Math.min(diminta, Math.max(0, Number(gp.pendua) || 0));
    const dijangka = diminta - pendua;
    const dihantar = Math.min(dijangka, Math.max(0, Number(gp.dilampirkan) || 0));
    const amaran = Array.isArray(gp.amaran)
        ? gp.amaran.filter(a => typeof a === 'string' && a.trim()).slice(0, 6)
        : [];
    const status = (dihantar < dijangka || amaran.length > 0 || dijangka === 0) ? 'kurang' : 'lengkap';
    return { status, dihantar, dijangka, pendua, amaran };
}

/**
 * ★ SEMAKAN RUANG GAMBAR SEBELUM RUN AGENT (keputusan user 2026-09-13).
 *
 * Agent yang menyimpan gambar perlukan ruang gambar tambahan KOSONG pada setiap
 * kad sasaran. Pulangkan kad yang TIDAK cukup ruang — pemanggil (larian pukal
 * tab My Agent di Workspace) menahan SELURUH larian bila senarai ini tidak kosong:
 * tiada automation dijalankan, kad ditanda, dan popup menerangkan cara betulkan.
 *
 * Kenapa perlu: tanpa semakan ini robot tetap membuka ChatGPT/Gemini, agent menjana
 * gambar (makan masa + kuota jana gambar pelanggan), kemudian gambar DITOLAK semasa
 * simpan kerana kad sudah penuh (`ditolak` dalam simpanGambarAgentKeKad). Semakan
 * semasa simpan itu KEKAL sebagai jaring terakhir (cth satu pusingan menjana lebih
 * daripada satu gambar).
 *
 * Ruang perlu = bilangan pusingan bertanda "Simpan gambar" (imageTurns.length),
 * dihadkan kepada MAKS_GAMBAR_TAMBAHAN — kalau tidak, agent yang menanda lebih
 * banyak pusingan daripada had akan menyekat SEMUA kad selama-lamanya, termasuk
 * kad kosong.
 * ⛔ Tab My Agent sahaja (keputusan user). Run Flow tidak menyimpan gambar ke kad.
 *
 * @param {Array<{id:string,name?:string,extraImageUrls?:string[]}>} kadSenarai
 * @param {number} bilPusinganGambar  bilangan pusingan bertanda simpan gambar
 * @returns {{id:string, nama:string, ada:number, perlu:number}[]}
 */
export function kadTiadaRuangGambar(kadSenarai, bilPusinganGambar) {
    const perlu = Math.min(MAKS_GAMBAR_TAMBAHAN, Math.max(0, Math.floor(Number(bilPusinganGambar) || 0)));
    if (perlu === 0 || !Array.isArray(kadSenarai)) return [];
    const hasil = [];
    for (const kad of kadSenarai) {
        if (!kad || typeof kad.id !== 'string' || !kad.id) continue;
        const ada = Math.min(
            MAKS_GAMBAR_TAMBAHAN,
            Array.isArray(kad.extraImageUrls) ? kad.extraImageUrls.filter((u) => typeof u === 'string' && u).length : 0,
        );
        if (MAKS_GAMBAR_TAMBAHAN - ada < perlu) hasil.push({ id: kad.id, nama: String(kad.name || ''), ada, perlu });
    }
    return hasil;
}
